import * as React from "react";
import {
  AppStateActions,
  useAppDispatch,
  useAppState,
} from "app/providers/AppStateProvider";
import { QRCodeSVG } from "qrcode.react";
import Text from "app/styles/Text";
import copyToClipboard from "app/lib/copyToClipboard";
import { ReactComponent as ChevronDown } from "app/assets/icons/chevron-down.svg";
import { ReactComponent as CopyIcon } from "app/assets/icons/copy.svg";
import Button from "app/components/Button";
import useCreatePayment from "../hooks/useCreatePayment";
// import AvailableNetworks from "./AvailableNetworks";
import { formatMoneyNoCurrency } from "app/lib/money";
import {
  FIREBASE_COIN_IMG_BASE_URL,
  PAYMENT_LINK,
  PAYMENT_REQUEST,
} from "app/constants/variables";
// import useRetrievePayment from "../hooks/useRetrievePayment";
import usePayPaymentLink from "../hooks/usePayPaymentLink";
import useListCurrencies from "app/views/Busha/TransferConfirm/hooks/useListCurrencies";
import SelectCurrencyMenu from "./SelectCurrencyMenu";
import MakePaymentShimmer from "./MakePaymentShimmer";
import LoaderContainer from "app/components/LoaderContainer";
import { useCallback, useMemo } from "react";
import { ClassNames } from "@emotion/react";
import tw from "twin.macro";

export default function MakePayment({
  onContinue,
}: {
  onContinue: () => void;
}) {
  const { merchant, source, sourceId, userPayload } =
    useAppState();
  const dispatch = useAppDispatch();

  const [copied, setCopied] = React.useState<string | boolean>(false);
  const { createPayment, data, isLoading, error } = useCreatePayment();

  const { currencies, isLoading: isLoadingCurrencies } = useListCurrencies({
    type: "crypto",
    payments_request: true,
    publicKey: merchant?.public_key,
  });

  const supportedCurrencies = useMemo(() => currencies?.filter(
    (c) => c.supported_networks.length > 0
  ), [currencies]);

  const networks = useMemo(() => supportedCurrencies?.find(
    (c) => c.code === merchant.source_currency
  )?.supported_networks, [supportedCurrencies, merchant.source_currency]);

  const {
    createPaymentLinkPayment,
    data: paymentLinkData,
    isLoading: isLoadingPaymentLink,
    error: errorPaymentLink,
  } = usePayPaymentLink();

  const [selectedNetwork, setSelectedNetwork] = React.useState(
    () => networks?.[0]?.network ?? ""
  );

  const responseData = {
    id: "",
    pay_in: data?.pay_in || paymentLinkData?.pay_in,
    source_currency: data?.source_currency || paymentLinkData?.source_currency,
    source_amount: data?.source_amount || paymentLinkData?.source_amount,
  };

  const handleContinue = React.useCallback(() => {
    dispatch({
      type: AppStateActions.setPayment,
      payload: data || paymentLinkData,
    });
    onContinue();
  }, [dispatch, data, onContinue, paymentLinkData]);

  // React.useEffect(() => {
  //   if (paymentData) {
  //     const status =
  //       paymentData?.timeline.events[paymentData?.timeline.events.length - 1]
  //         .done;
  //     if (status) {
  //       handleContinue();
  //     }
  //   }
  // }, [paymentData, handleContinue]);

  const onCopy = (value: string) => {
    copyToClipboard(value).then(() => {
      setCopied(value);
    });
  };

  React.useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  }, [copied]);

  const handleCreatePayment = useCallback(() => {
    createPayment({
      additional_info: merchant?.meta,
      reference: merchant?.reference,
      dry_run: false,
      source_currency: merchant?.source_currency as string,
      quote_currency: merchant?.quote_currency || "",
      quote_amount: merchant?.quote_amount || "",
      target_currency: merchant?.target_currency || "",
      publicKey: merchant?.public_key || "",
      pay_in: {
        type: "address",
        network: (selectedNetwork || networks?.[0]?.network) ?? "",
      },
    });
  }, [merchant, selectedNetwork, networks, createPayment]);

  const handleCreatePaymentLinkPayment = useCallback((sourceId: string) => {
    return createPaymentLinkPayment({
      paymentId: sourceId,
      source_currency: merchant.source_currency!,
      target_currency: merchant.target_currency!,
      quote_currency: merchant?.quote_currency || "",
      quote_amount: merchant?.quote_amount || "",
      requested_info: merchant?.meta || {
        email: userPayload?.email!,
        firstname: userPayload?.firstname,
        lastname: userPayload?.lastname,
        ...(merchant?.meta?.phone_number && {
          phone: merchant?.meta?.phone_number,
        }),
      },
      dry_run: false,
      pay_in: {
        type: "address",
        network: (selectedNetwork || networks?.[0]?.network) ?? "",
      },
    });
  }, [merchant, userPayload, selectedNetwork, networks, createPaymentLinkPayment]);

  const handleNetworkChange = (network: string) => {
    setSelectedNetwork(network);
  };

  const handleCurrencyChange = (currencyCode: string) => {
    dispatch({
      type: AppStateActions.setMerchant,
      payload: {
        ...merchant,
        source_currency: currencyCode,
      },
    });

    setSelectedNetwork("");
  };

  const createPaymentRequest = useCallback(() => {
    const isPaymentRequest = source === PAYMENT_REQUEST;
    const isPaymentLink = source === PAYMENT_LINK;

    if (isPaymentRequest || !source) {
      handleCreatePayment();
    } else if (sourceId && isPaymentLink) {
      handleCreatePaymentLinkPayment(sourceId);
    }
  }, [source, sourceId, handleCreatePayment, handleCreatePaymentLinkPayment]);

  React.useEffect(() => {
    if (networks?.length) {
      createPaymentRequest();
    }
  }, [merchant.source_currency, networks?.length, createPaymentRequest]);

  // if (!activeAsset) return null;

  const sourceCurrencyName =
    supportedCurrencies?.find((item) => item.code === merchant.source_currency)
      ?.name ?? merchant.source_currency;

  const networkName = selectedNetwork || networks?.[0]?.network || "";

  return (
    <>
      <LoaderContainer
        tw="h-full justify-start"
        loading={isLoading || isLoadingPaymentLink || isLoadingCurrencies}
        // error={!!error || !!errorPaymentLink}
        errorMessage={error! || errorPaymentLink!}
        onRetry={createPaymentRequest}
        customLoader={<MakePaymentShimmer />}
      >
        <div tw="h-full overflow-y-auto gap-[24px] flex flex-col w-full">
          <div tw="flex flex-col gap-[12px]">
            <SelectCurrencyMenu
              supportedCurrencies={supportedCurrencies?.map((item) => ({
                name: item.name,
                code: item.code,
              }))}
              handleCurrencyChange={handleCurrencyChange}
              value={merchant.source_currency!}
            >
              <div tw="p-[16px] rounded-[16px] bg-containment-tertiary flex items-center justify-between">
                <div tw="flex items-center gap-[12px]">
                  <img
                    src={`${FIREBASE_COIN_IMG_BASE_URL}${merchant?.source_currency}.svg?alt=media`}
                    alt="crypto"
                    tw="w-[40px] h-[40px] rounded-full"
                  />
                  <div>
                    <Text small tw="mb-[2px] text-left" subdued>
                      Pay with{" "}
                    </Text>
                    <Text medium semiBold tw="text-left">
                      {sourceCurrencyName}
                    </Text>
                  </div>
                </div>

                <ChevronDown tw="w-[24px] h-[24px] text-text-mid" />
              </div>
            </SelectCurrencyMenu>

            <div tw="bg-containment-tertiary py-[30px] px-4 flex items-center justify-center rounded-[16px]">
              <QRCodeSVG
                tw="w-[200px] h-[200px] mx-auto"
                value={responseData?.pay_in?.address ?? ""}
              />
            </div>

            <div tw="bg-containment-tertiary rounded-[16px] p-[16px] flex flex-col gap-[20px]">
              <div tw="flex items-start justify-between gap-[16px]">
                <div tw="max-w-[calc(100% - 30px)]">
                  <Text tw="mb-[4px] " subdued>
                    {responseData.source_currency} -{" "}
                    {responseData.pay_in?.network} address
                  </Text>
                  <Text
                    tw="overflow-hidden overflow-ellipsis max-w-full whitespace-nowrap"
                    mediumBold
                  >
                    {responseData?.pay_in?.address}
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(responseData?.pay_in?.address!)}
                >
                  {copied === responseData?.pay_in?.address ? (
                    "Copied"
                  ) : (
                    <CopyIcon tw="w-[24px] h-[24px] text-text-mid" />
                  )}
                </button>
              </div>

              <div tw="flex items-center justify-between gap-[16px]">
                <Text subdued>Network</Text>
                <div tw="flex items-center gap-[8px]">
                  {/* <div tw="flex items-center w-[24px] h-[24px] rounded-full bg-surfaceInputField"></div>{" "} */}
                  <img
                    src={`${FIREBASE_COIN_IMG_BASE_URL}${networkName}.svg?alt=media`}
                    alt="crypto"
                    tw="w-[24px] h-[24px]  rounded-full"
                  />
                  <Text semiBold>{networkName}</Text>
                  {networks && networks?.length > 1 && (
                    <ClassNames>
                      {({ css }) => (
                        <SelectCurrencyMenu
                          noSearch
                          supportedCurrencies={networks?.map((item) => ({
                            name: item.network,
                            code: item.network,
                            description: `Fee: ${item.withdrawal_fee} ${merchant?.source_currency}`,
                          }))}
                          handleCurrencyChange={handleNetworkChange}
                          value={networkName}
                          dropdownClassName={css(tw`w-[350px] md:w-[400px] max-w-full`)}
                          anchor="bottom end"
                        >
                          <ChevronDown color="#0D0D0D" tw="w-[20px] h-[20px]" />
                        </SelectCurrencyMenu>
                      )}
                    </ClassNames>
                  )}
                  {/* {activeAsset?.chains?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setChainModalVisible(true)}
                    >
                      <ChevronDown color="text-high" tw="w-[20px] h-[20px]" />
                    </button>
                  )} */}
                </div>
              </div>

              <div tw="flex items-center justify-between gap-[16px]">
                <Text subdued>Amount to send</Text>
                <div tw="flex items-center gap-[8px]">
                  <Text semiBold>
                    {formatMoneyNoCurrency(
                      +responseData?.source_amount!,
                      "crypto"
                    )}{" "}
                    {responseData?.source_currency}{" "}
                  </Text>
                  <button
                    type="button"
                    onClick={() => onCopy(responseData?.source_amount!)}
                  >
                    {copied === responseData?.source_amount ? (
                      "Copied"
                    ) : (
                      <CopyIcon tw="w-[24px] h-[24px] text-text-mid" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div tw="w-full">
            <Button
              variant="primary"
              size="xBig"
              fullWidth
              onClick={handleContinue}
            >
              I’ve made payment
            </Button>
          </div>
        </div>

        {/* <AvailableNetworks
        isOpen={chainModal}
        currency={activeAsset?.currency_id}
        onClose={() => {
          setChainModalVisible(false);
        }}
        chains={
          activeAsset?.supportedNetworks?.filter(
            (item) => item.status === "active"
          ) || []
        }
        onChange={(value) => {
          setChain(value);
        }}
      /> */}
      </LoaderContainer>
    </>
  );
}
